import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import EmailTemplateService from '../../../utils/emailTemplateService';

const TemplatePreview = ({ template, onBack, onEdit }) => {
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customVariables, setCustomVariables] = useState({});
  const [previewMode, setPreviewMode] = useState('html'); // 'html', 'text', 'subject'

  useEffect(() => {
    if (template) {
      loadPreview();
    }
  }, [template]);

  const loadPreview = async (customData = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await EmailTemplateService.previewTemplate(
        template.template_type, 
        customData
      );
      
      if (result.success) {
        setPreviewData(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVariableChange = (variable, value) => {
    const newCustomVariables = {
      ...customVariables,
      [variable]: value
    };
    setCustomVariables(newCustomVariables);
    
    // Debounce the preview update
    clearTimeout(window.previewTimeout);
    window.previewTimeout = setTimeout(() => {
      loadPreview(newCustomVariables);
    }, 500);
  };

  const resetToDefaults = () => {
    setCustomVariables({});
    loadPreview();
  };

  const getTemplateTypeInfo = () => {
    const options = EmailTemplateService.getTemplateTypeOptions();
    return options.find(opt => opt.value === template.template_type) || {
      label: template.template_type,
      description: 'Custom template'
    };
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Loader2" size={20} className="animate-spin" />
            <span>Loading preview...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Preview Error</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <div className="flex items-center justify-center space-x-3">
            <Button variant="outline" onClick={onBack}>
              Back to List
            </Button>
            <Button onClick={() => loadPreview()}>
              Retry Preview
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const typeInfo = getTemplateTypeInfo();
  const variables = Array.isArray(template.variables) ? template.variables : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Template Preview</h2>
          <p className="text-muted-foreground mt-1">
            {template.name} - {typeInfo.label}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="RotateCcw"
            onClick={resetToDefaults}
          >
            Reset to Defaults
          </Button>
          <Button
            variant="outline"
            iconName="ArrowLeft"
            onClick={onBack}
          >
            Back to List
          </Button>
          <Button
            iconName="Edit"
            onClick={onEdit}
          >
            Edit Template
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Variable Controls */}
        <div className="lg:col-span-1">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-medium text-foreground mb-4">Preview Variables</h3>
            
            {variables.length > 0 ? (
              <div className="space-y-4">
                {variables.map((variable) => {
                  const defaultValue = EmailTemplateService.getDefaultSampleData(template.template_type)[variable] || '';
                  
                  return (
                    <div key={variable}>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        {variable}
                      </label>
                      <Input
                        value={customVariables[variable] || defaultValue}
                        onChange={(e) => handleVariableChange(variable, e.target.value)}
                        placeholder={`Enter ${variable}...`}
                        size="sm"
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                No variables found in this template.
              </p>
            )}

            {/* Template Info */}
            <div className="mt-6 pt-6 border-t border-border">
              <h4 className="text-sm font-medium text-foreground mb-3">Template Info</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className={template.is_active ? 'text-accent' : 'text-muted-foreground'}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="text-foreground">{typeInfo.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Variables:</span>
                  <span className="text-foreground">{variables.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Updated:</span>
                  <span className="text-foreground">
                    {new Date(template.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Content */}
        <div className="lg:col-span-3">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Preview Mode Tabs */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setPreviewMode('subject')}
                  className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    previewMode === 'subject'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <Icon name="Type" size={16} />
                  <span>Subject Line</span>
                </button>
                <button
                  onClick={() => setPreviewMode('html')}
                  className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    previewMode === 'html'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <Icon name="Code" size={16} />
                  <span>HTML Preview</span>
                </button>
                {previewData?.text && (
                  <button
                    onClick={() => setPreviewMode('text')}
                    className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      previewMode === 'text'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name="FileText" size={16} />
                    <span>Text Version</span>
                  </button>
                )}
              </nav>
            </div>

            {/* Preview Content */}
            <div className="p-6">
              {previewMode === 'subject' && (
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-4">Subject Line Preview</h3>
                  <div className="bg-muted/30 border border-border rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
                      <Icon name="Mail" size={16} />
                      <span>Subject:</span>
                    </div>
                    <div className="text-foreground font-medium">
                      {previewData?.subject || 'No subject'}
                    </div>
                  </div>
                </div>
              )}

              {previewMode === 'html' && (
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-4">HTML Email Preview</h3>
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted/30 px-4 py-2 border-b border-border">
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Globe" size={16} />
                        <span>Email Client Preview</span>
                      </div>
                    </div>
                    <div className="bg-white">
                      <iframe
                        srcDoc={previewData?.html || '<p>No HTML content</p>'}
                        className="w-full h-96 border-0"
                        title="Email Preview"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </div>
              )}

              {previewMode === 'text' && previewData?.text && (
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-4">Plain Text Preview</h3>
                  <div className="bg-muted/30 border border-border rounded-lg p-4">
                    <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                      {previewData.text}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;
